import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Collapse,
  TablePagination,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as CreateIcon,
  Edit as UpdateIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { authenticatedFetch } from '../../utils/api';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface AuditLog {
  id: number;
  user_id: number | null;
  user_email: string;
  resource_type: string;
  resource_id: number;
  resource_name: string | null;
  action: string;
  action_label: string;
  changes: Record<string, unknown>;
  ip_address: string | null;
  created_at: string;
}

interface User {
  id: number;
  email: string;
}

interface ChangeValue {
  from?: unknown;
  to?: unknown;
}

// Helper functions
const getActionIcon = (action: string) => {
  switch (action) {
    case 'create':
      return <CreateIcon fontSize="small" />;
    case 'update':
      return <UpdateIcon fontSize="small" />;
    case 'delete':
      return <DeleteIcon fontSize="small" />;
    default:
      return null;
  }
};

const getActionColor = (action: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (action) {
    case 'create':
      return 'success';
    case 'update':
      return 'warning';
    case 'delete':
      return 'error';
    default:
      return 'default';
  }
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const isChangeObject = (value: unknown): value is ChangeValue => {
  return typeof value === 'object' && value !== null && ('from' in value || 'to' in value);
};

// Mobile card component for audit log
const AuditLogCard = ({ log }: { log: AuditLog }) => {
  const [open, setOpen] = useState(false);
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: hasChanges ? 1 : 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Chip
            icon={getActionIcon(log.action) || undefined}
            label={log.action_label}
            color={getActionColor(log.action)}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {formatDate(log.created_at)}
          </Typography>
        </Box>

        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
          {log.resource_type}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {log.resource_name || `#${log.resource_id}`}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          by {log.user_email}
        </Typography>
      </CardContent>

      {hasChanges && (
        <>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1,
              cursor: 'pointer',
              bgcolor: open ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setOpen(!open)}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="primary">
                View Changes ({Object.keys(log.changes).length})
              </Typography>
              <IconButton size="small">
                {open ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Box>
          </Box>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 2 }}>
              <Stack spacing={1.5}>
                {Object.entries(log.changes).map(([field, change]) => {
                  const hasFromTo = isChangeObject(change);
                  return (
                    <Box key={field} sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {field.replace(/_/g, ' ')}
                      </Typography>
                      {hasFromTo ? (
                        <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                              From:
                            </Typography>
                            <Typography variant="body2" color="error.main" sx={{ wordBreak: 'break-word', flex: 1 }}>
                              {formatValue(change.from)}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                              To:
                            </Typography>
                            <Typography variant="body2" color="success.main" sx={{ wordBreak: 'break-word', flex: 1 }}>
                              {formatValue(change.to)}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="success.main" sx={{ wordBreak: 'break-word', mt: 0.5 }}>
                          {formatValue(change)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
};

// Desktop table row component
const AuditLogRow = ({ log }: { log: AuditLog }) => {
  const [open, setOpen] = useState(false);
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;

  return (
    <>
      <TableRow
        sx={{ '& > *': { borderBottom: 'unset' }, cursor: hasChanges ? 'pointer' : 'default' }}
        onClick={() => hasChanges && setOpen(!open)}
        hover
      >
        <TableCell sx={{ width: 40 }}>
          {hasChanges && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
              {open ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {log.user_email}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            icon={getActionIcon(log.action) || undefined}
            label={log.action_label}
            color={getActionColor(log.action)}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {log.resource_type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {log.resource_name || `#${log.resource_id}`}
          </Typography>
        </TableCell>
        <TableCell>
          {formatDate(log.created_at)}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
          <Typography variant="caption" color="text.secondary">
            {log.ip_address || '-'}
          </Typography>
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Changes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(log.changes).map(([field, change]) => {
                      const hasFromTo = isChangeObject(change);
                      return (
                        <TableRow key={field}>
                          <TableCell component="th" scope="row">
                            <Typography variant="body2" fontWeight={500}>
                              {field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error.main" sx={{ wordBreak: 'break-all' }}>
                              {hasFromTo ? formatValue(change.from) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="success.main" sx={{ wordBreak: 'break-all' }}>
                              {hasFromTo ? formatValue(change.to) : formatValue(change)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditLogList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [resourceTypes, setResourceTypes] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Filters
  const [resourceType, setResourceType] = useState<string>('');
  const [actionType, setActionType] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const { t } = useTranslation();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('per_page', String(rowsPerPage));
      if (resourceType) params.append('resource_type', resourceType);
      if (actionType) params.append('action_type', actionType);
      if (userId) params.append('user_id', userId);
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await authenticatedFetch(`${API_URL}/audit_logs?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();
      setLogs(data);
      setTotalCount(parseInt(response.headers.get('X-Total-Count') || '0', 10));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, resourceType, actionType, userId, search, fromDate, toDate]);

  const fetchFilters = useCallback(async () => {
    try {
      const [typesRes, usersRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/audit_logs/resource_types`),
        authenticatedFetch(`${API_URL}/audit_logs/users`),
      ]);

      if (typesRes.ok) {
        setResourceTypes(await typesRes.json());
      }
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch filters:', err);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setResourceType('');
    setActionType('');
    setUserId('');
    setSearch('');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const hasFilters = resourceType || actionType || userId || search || fromDate || toDate;

  const activeFilterCount = [resourceType, actionType, userId, fromDate, toDate].filter(Boolean).length;

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Filters - Collapsible on mobile */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Search always visible */}
          <TextField
            fullWidth
            size="small"
            label={t('search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Collapsible filters */}
          <Accordion
            expanded={filtersExpanded}
            onChange={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterIcon fontSize="small" />
                <Typography variant="body2">
                  Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Resource</InputLabel>
                  <Select
                    value={resourceType}
                    label="Resource"
                    onChange={(e) => { setResourceType(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {resourceTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>{t('action')}</InputLabel>
                  <Select
                    value={actionType}
                    label={t('action')}
                    onChange={(e) => { setActionType(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="create">Created</MenuItem>
                    <MenuItem value="update">Updated</MenuItem>
                    <MenuItem value="delete">Deleted</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={userId}
                    label="User"
                    onChange={(e) => { setUserId(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>{user.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From"
                    value={fromDate}
                    onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To"
                    value={toDate}
                    onChange={(e) => { setToDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                {hasFilters && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                    fullWidth
                  >
                    {t('clearFilters')}
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('search')}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Resource</InputLabel>
                  <Select
                    value={resourceType}
                    label="Resource"
                    onChange={(e) => { setResourceType(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {resourceTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('action')}</InputLabel>
                  <Select
                    value={actionType}
                    label={t('action')}
                    onChange={(e) => { setActionType(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="create">Created</MenuItem>
                    <MenuItem value="update">Updated</MenuItem>
                    <MenuItem value="delete">Deleted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={userId}
                    label="User"
                    onChange={(e) => { setUserId(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>{user.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {hasFilters && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      onClick={clearFilters}
                      size="small"
                      color="primary"
                      startIcon={<ClearIcon />}
                    >
                      {t('clearFilters')}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* Mobile: Card-based layout */
        <Box>
          {logs.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  {t('noData')}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              {logs.map((log) => <AuditLogCard key={log.id} log={log} />)}
              <Card>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage=""
                  sx={{
                    '.MuiTablePagination-selectLabel': { display: 'none' },
                    '.MuiTablePagination-displayedRows': { fontSize: '0.875rem' },
                  }}
                />
              </Card>
            </>
          )}
        </Box>
      ) : (
        /* Desktop: Table layout */
        <Paper>
          <TableContainer>
            <Table aria-label="audit logs table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>User</TableCell>
                  <TableCell>{t('action')}</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>{t('date')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {t('noData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => <AuditLogRow key={log.id} log={log} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}
    </Container>
  );
};

export default AuditLogList;
